/**
 * V47.15: 휴가 데이터 테스트 유틸리티 페이지
 *
 * system_admin 전용 관리 페이지
 * - 휴가 데이터 초기화 (직원/회사 단위)
 * - 휴가 발생 재계산 (used=0 초기화)
 * - 테스트 시간 설정
 */

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import {
  Trash2,
  RefreshCcw,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader,
  Database,
  Search,
  User,
} from 'lucide-react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { EmployeePickerModal } from '../hr/components/EmployeePickerModal';

// ============================================
// Types
// ============================================

interface ResetLeaveDataResponse {
  success: boolean;
  message: string;
  deletedCounts: {
    leaveBalances: number;
    leaveHistories: number;
    leaveEntitlements: number;
    leaveApprovals: number;
  };
  targetInfo: {
    scope: string;
    employeeId?: string;
    companyCode?: string;
    employeeCount?: number;
  };
}

interface RecomputeLeaveAccrualResponse {
  success: boolean;
  message: string;
  processedCount: number;
  results: Array<{
    employeeId: string;
    employeeName?: string;
    granted: number;
    validFrom: string;
    validUntil: string;
  }>;
  failures: Array<{
    employeeId: string;
    error: string;
  }>;
}

interface SetTestNowResponse {
  success: boolean;
  message: string;
  testNow: string | null;
  previousTestNow: string | null;
}

// ============================================
// Component
// ============================================

export function AdminTestUtilitiesPage() {
  // Reset Leave Data 상태
  const [resetScope, setResetScope] = useState<'employee' | 'company'>('employee');
  const [resetEmployeeId, setResetEmployeeId] = useState('');
  const [resetEmployeeName, setResetEmployeeName] = useState(''); // V47.15.2: 선택된 직원 이름
  const [resetCompanyCode, setResetCompanyCode] = useState<'KR' | 'CN'>('KR');
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [alsoDeleteLeaveApprovals, setAlsoDeleteLeaveApprovals] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState<ResetLeaveDataResponse | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetEmployeePickerOpen, setResetEmployeePickerOpen] = useState(false); // V47.15.2

  // Recompute Leave Accrual 상태
  const [recomputeScope, setRecomputeScope] = useState<'employee' | 'company'>('employee');
  const [recomputeEmployeeId, setRecomputeEmployeeId] = useState('');
  const [recomputeEmployeeName, setRecomputeEmployeeName] = useState(''); // V47.15.2: 선택된 직원 이름
  const [recomputeCompanyCode, setRecomputeCompanyCode] = useState<'KR' | 'CN'>('KR');
  const [recomputeConfirmText, setRecomputeConfirmText] = useState('');
  const [recomputeLoading, setRecomputeLoading] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState<RecomputeLeaveAccrualResponse | null>(null);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);
  const [recomputeEmployeePickerOpen, setRecomputeEmployeePickerOpen] = useState(false); // V47.15.2

  // Test Now 상태
  const [testNowInput, setTestNowInput] = useState('');
  const [currentTestNow, setCurrentTestNow] = useState<string | null>(null);
  const [testNowLoading, setTestNowLoading] = useState(false);
  const [testNowResult, setTestNowResult] = useState<SetTestNowResponse | null>(null);
  const [testNowError, setTestNowError] = useState<string | null>(null);

  // 실시간 testClock 구독
  useEffect(() => {
    const db = getFirestore();
    const testClockRef = doc(db, 'systemConfigurations', 'testClock');

    const unsubscribe = onSnapshot(testClockRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data?.enabled && data?.testNow) {
          setCurrentTestNow(data.testNow);
        } else {
          setCurrentTestNow(null);
        }
      } else {
        setCurrentTestNow(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // ============================================
  // Reset Leave Data
  // ============================================

  const handleResetLeaveData = async () => {
    if (resetScope === 'employee' && !resetEmployeeId.trim()) {
      setResetError('직원 ID를 입력해주세요.');
      return;
    }

    if (resetScope === 'company' && resetConfirmText !== 'RESET') {
      setResetError('회사 전체 삭제를 확인하려면 "RESET"을 입력하세요.');
      return;
    }

    setResetLoading(true);
    setResetError(null);
    setResetResult(null);

    try {
      const functions = getFunctions();
      const resetFn = httpsCallable<any, ResetLeaveDataResponse>(functions, 'adminResetLeaveData');

      const response = await resetFn({
        scope: resetScope,
        employeeId: resetScope === 'employee' ? resetEmployeeId.trim() : undefined,
        companyCode: resetScope === 'company' ? resetCompanyCode : undefined,
        alsoDeleteLeaveApprovals,
        confirmText: resetScope === 'company' ? resetConfirmText : undefined,
      });

      setResetResult(response.data);

      if (response.data.success) {
        setResetEmployeeId('');
        setResetEmployeeName(''); // V47.15.2
        setResetConfirmText('');
      }
    } catch (err: any) {
      console.error('Reset leave data error:', err);
      setResetError(err.message || '알 수 없는 오류가 발생했습니다.');
    } finally {
      setResetLoading(false);
    }
  };

  // ============================================
  // Recompute Leave Accrual
  // ============================================

  const handleRecomputeLeaveAccrual = async () => {
    if (recomputeScope === 'employee' && !recomputeEmployeeId.trim()) {
      setRecomputeError('직원 ID를 입력해주세요.');
      return;
    }

    if (recomputeScope === 'company' && recomputeConfirmText !== 'RECOMPUTE') {
      setRecomputeError('회사 전체 재계산을 확인하려면 "RECOMPUTE"를 입력하세요.');
      return;
    }

    setRecomputeLoading(true);
    setRecomputeError(null);
    setRecomputeResult(null);

    try {
      const functions = getFunctions();
      const recomputeFn = httpsCallable<any, RecomputeLeaveAccrualResponse>(functions, 'adminRecomputeLeaveAccrual');

      const response = await recomputeFn({
        scope: recomputeScope,
        employeeId: recomputeScope === 'employee' ? recomputeEmployeeId.trim() : undefined,
        companyCode: recomputeScope === 'company' ? recomputeCompanyCode : undefined,
        confirmText: recomputeScope === 'company' ? recomputeConfirmText : undefined,
      });

      setRecomputeResult(response.data);

      if (response.data.success) {
        setRecomputeEmployeeId('');
        setRecomputeEmployeeName(''); // V47.15.2
        setRecomputeConfirmText('');
      }
    } catch (err: any) {
      console.error('Recompute leave accrual error:', err);
      setRecomputeError(err.message || '알 수 없는 오류가 발생했습니다.');
    } finally {
      setRecomputeLoading(false);
    }
  };

  // ============================================
  // Set Test Now
  // ============================================

  const handleSetTestNow = async () => {
    setTestNowLoading(true);
    setTestNowError(null);
    setTestNowResult(null);

    try {
      const functions = getFunctions();
      const setTestNowFn = httpsCallable<any, SetTestNowResponse>(functions, 'adminSetTestNow');

      const testNow = testNowInput.trim() || null;

      const response = await setTestNowFn({ testNow });

      setTestNowResult(response.data);

      if (response.data.success) {
        setTestNowInput('');
      }
    } catch (err: any) {
      console.error('Set test now error:', err);
      setTestNowError(err.message || '알 수 없는 오류가 발생했습니다.');
    } finally {
      setTestNowLoading(false);
    }
  };

  const handleClearTestNow = async () => {
    setTestNowInput('');
    setTestNowLoading(true);
    setTestNowError(null);
    setTestNowResult(null);

    try {
      const functions = getFunctions();
      const setTestNowFn = httpsCallable<any, SetTestNowResponse>(functions, 'adminSetTestNow');

      const response = await setTestNowFn({ testNow: null });

      setTestNowResult(response.data);
    } catch (err: any) {
      console.error('Clear test now error:', err);
      setTestNowError(err.message || '알 수 없는 오류가 발생했습니다.');
    } finally {
      setTestNowLoading(false);
    }
  };

  // ============================================
  // Render
  // ============================================

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Database size={24} />
        V47.15: 휴가 데이터 테스트 유틸리티
      </h1>
      <p style={{ color: '#666', marginBottom: '24px' }}>
        테스트/디버깅을 위한 휴가 데이터 관리 도구입니다. (system_admin 전용)
      </p>

      {/* 경고 박스 */}
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
      }}>
        <AlertTriangle size={24} color="#856404" />
        <div>
          <div style={{ fontWeight: 'bold', color: '#856404', marginBottom: '4px' }}>
            ⚠️ 주의: 데이터가 영구 변경/삭제됩니다!
          </div>
          <div style={{ color: '#856404', fontSize: '14px' }}>
            이 유틸리티는 테스트 환경에서만 사용하세요. 운영 환경에서 사용 시 복구 불가능한 데이터 손실이 발생할 수 있습니다.
          </div>
        </div>
      </div>

      {/* 현재 테스트 시간 표시 */}
      {currentTestNow && (
        <div style={{
          background: '#d4edda',
          border: '1px solid #28a745',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
        }}>
          <Clock size={24} color="#155724" />
          <div>
            <div style={{ fontWeight: 'bold', color: '#155724' }}>
              테스트 시간 활성화됨
            </div>
            <div style={{ color: '#155724', fontSize: '14px' }}>
              현재 시스템이 사용하는 시간: <strong>{currentTestNow}</strong>
            </div>
          </div>
        </div>
      )}

      {/* 3개 카드 레이아웃 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>

        {/* 카드 1: Reset Leave Data */}
        <div style={{
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '20px',
          background: '#fff',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Trash2 size={20} color="#dc3545" />
            휴가 데이터 초기화
          </h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
            leaveBalances, leaveHistories, leaveEntitlements를 삭제합니다.
          </p>

          {/* Scope 선택 */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>범위</label>
            <select
              value={resetScope}
              onChange={(e) => setResetScope(e.target.value as 'employee' | 'company')}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="employee">개별 직원</option>
              <option value="company">회사 전체</option>
            </select>
          </div>

          {/* V47.15.2: 직원 선택 UI 또는 회사 코드 */}
          {resetScope === 'employee' ? (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>직원 선택</label>
              <button
                type="button"
                onClick={() => setResetEmployeePickerOpen(true)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  background: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                {resetEmployeeId ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <User size={16} />
                    <span>{resetEmployeeName || resetEmployeeId}</span>
                  </span>
                ) : (
                  <span style={{ color: '#999', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Search size={16} />
                    직원 검색...
                  </span>
                )}
              </button>
              {resetEmployeeId && (
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  ID: {resetEmployeeId}
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>회사 코드</label>
                <select
                  value={resetCompanyCode}
                  onChange={(e) => setResetCompanyCode(e.target.value as 'KR' | 'CN')}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                >
                  <option value="KR">KR (한국)</option>
                  <option value="CN">CN (중국)</option>
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  확인 텍스트 (회사 전체 삭제 시 "RESET" 입력)
                </label>
                <input
                  type="text"
                  value={resetConfirmText}
                  onChange={(e) => setResetConfirmText(e.target.value)}
                  placeholder="RESET"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
            </>
          )}

          {/* 휴가 결재 삭제 옵션 */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={alsoDeleteLeaveApprovals}
                onChange={(e) => setAlsoDeleteLeaveApprovals(e.target.checked)}
              />
              휴가 결재 데이터도 함께 삭제
            </label>
          </div>

          {/* 실행 버튼 */}
          <button
            onClick={handleResetLeaveData}
            disabled={resetLoading}
            style={{
              width: '100%',
              padding: '10px',
              background: resetLoading ? '#ccc' : '#dc3545',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: resetLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {resetLoading ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
            {resetLoading ? '처리 중...' : '휴가 데이터 초기화'}
          </button>

          {/* 결과/에러 표시 */}
          {resetError && (
            <div style={{ marginTop: '12px', padding: '12px', background: '#f8d7da', borderRadius: '4px', color: '#721c24' }}>
              <XCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
              {resetError}
            </div>
          )}
          {resetResult && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: resetResult.success ? '#d4edda' : '#f8d7da',
              borderRadius: '4px',
              color: resetResult.success ? '#155724' : '#721c24',
            }}>
              {resetResult.success ? <CheckCircle size={16} style={{ display: 'inline', marginRight: '8px' }} /> : <XCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />}
              {resetResult.message}
              <div style={{ marginTop: '8px', fontSize: '12px' }}>
                <div>leaveBalances: {resetResult.deletedCounts.leaveBalances}건</div>
                <div>leaveHistories: {resetResult.deletedCounts.leaveHistories}건</div>
                <div>leaveEntitlements: {resetResult.deletedCounts.leaveEntitlements}건</div>
                <div>leaveApprovals: {resetResult.deletedCounts.leaveApprovals}건</div>
              </div>
            </div>
          )}
        </div>

        {/* 카드 2: Recompute Leave Accrual */}
        <div style={{
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '20px',
          background: '#fff',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RefreshCcw size={20} color="#007bff" />
            휴가 발생 재계산
          </h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
            leaveEntitlements 기반으로 granted를 재설정하고, used=0으로 초기화합니다.
          </p>

          {/* Scope 선택 */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>범위</label>
            <select
              value={recomputeScope}
              onChange={(e) => setRecomputeScope(e.target.value as 'employee' | 'company')}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="employee">개별 직원</option>
              <option value="company">회사 전체</option>
            </select>
          </div>

          {/* V47.15.2: 직원 선택 UI 또는 회사 코드 */}
          {recomputeScope === 'employee' ? (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>직원 선택</label>
              <button
                type="button"
                onClick={() => setRecomputeEmployeePickerOpen(true)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  background: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                {recomputeEmployeeId ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <User size={16} />
                    <span>{recomputeEmployeeName || recomputeEmployeeId}</span>
                  </span>
                ) : (
                  <span style={{ color: '#999', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Search size={16} />
                    직원 검색...
                  </span>
                )}
              </button>
              {recomputeEmployeeId && (
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                  ID: {recomputeEmployeeId}
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>회사 코드</label>
                <select
                  value={recomputeCompanyCode}
                  onChange={(e) => setRecomputeCompanyCode(e.target.value as 'KR' | 'CN')}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                >
                  <option value="KR">KR (한국)</option>
                  <option value="CN">CN (중국)</option>
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  확인 텍스트 (회사 전체 시 "RECOMPUTE" 입력)
                </label>
                <input
                  type="text"
                  value={recomputeConfirmText}
                  onChange={(e) => setRecomputeConfirmText(e.target.value)}
                  placeholder="RECOMPUTE"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
            </>
          )}

          {/* 실행 버튼 */}
          <button
            onClick={handleRecomputeLeaveAccrual}
            disabled={recomputeLoading}
            style={{
              width: '100%',
              padding: '10px',
              background: recomputeLoading ? '#ccc' : '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: recomputeLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {recomputeLoading ? <Loader size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
            {recomputeLoading ? '처리 중...' : '휴가 발생 재계산'}
          </button>

          {/* 결과/에러 표시 */}
          {recomputeError && (
            <div style={{ marginTop: '12px', padding: '12px', background: '#f8d7da', borderRadius: '4px', color: '#721c24' }}>
              <XCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
              {recomputeError}
            </div>
          )}
          {recomputeResult && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: recomputeResult.success ? '#d4edda' : '#f8d7da',
              borderRadius: '4px',
              color: recomputeResult.success ? '#155724' : '#721c24',
            }}>
              {recomputeResult.success ? <CheckCircle size={16} style={{ display: 'inline', marginRight: '8px' }} /> : <XCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />}
              {recomputeResult.message}
              {recomputeResult.results.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '12px', maxHeight: '150px', overflowY: 'auto' }}>
                  {recomputeResult.results.slice(0, 10).map((r, idx) => (
                    <div key={idx}>
                      {r.employeeName || r.employeeId}: granted={r.granted} ({r.validFrom} ~ {r.validUntil})
                    </div>
                  ))}
                  {recomputeResult.results.length > 10 && (
                    <div style={{ fontStyle: 'italic' }}>...외 {recomputeResult.results.length - 10}명</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 카드 3: Set Test Now */}
        <div style={{
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '20px',
          background: '#fff',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={20} color="#28a745" />
            테스트 시간 설정
          </h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
            시스템에서 사용하는 "현재 시간"을 특정 날짜로 고정합니다.
          </p>

          {/* 현재 상태 */}
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            background: currentTestNow ? '#e2e3e5' : '#f8f9fa',
            borderRadius: '4px',
            fontSize: '14px',
          }}>
            <strong>현재 상태:</strong>{' '}
            {currentTestNow ? (
              <span style={{ color: '#28a745' }}>{currentTestNow}</span>
            ) : (
              <span style={{ color: '#6c757d' }}>실제 시간 사용 중</span>
            )}
          </div>

          {/* 테스트 시간 입력 */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              테스트 시간 (YYYY-MM-DD 또는 ISO 8601)
            </label>
            <input
              type="text"
              value={testNowInput}
              onChange={(e) => setTestNowInput(e.target.value)}
              placeholder="예: 2025-06-15 또는 2025-06-15T09:00:00"
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>

          {/* 버튼들 */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSetTestNow}
              disabled={testNowLoading || !testNowInput.trim()}
              style={{
                flex: 1,
                padding: '10px',
                background: testNowLoading || !testNowInput.trim() ? '#ccc' : '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontWeight: 'bold',
                cursor: testNowLoading || !testNowInput.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {testNowLoading ? <Loader size={16} className="animate-spin" /> : <Clock size={16} />}
              설정
            </button>
            <button
              onClick={handleClearTestNow}
              disabled={testNowLoading || !currentTestNow}
              style={{
                flex: 1,
                padding: '10px',
                background: testNowLoading || !currentTestNow ? '#ccc' : '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontWeight: 'bold',
                cursor: testNowLoading || !currentTestNow ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {testNowLoading ? <Loader size={16} className="animate-spin" /> : <XCircle size={16} />}
              해제
            </button>
          </div>

          {/* 결과/에러 표시 */}
          {testNowError && (
            <div style={{ marginTop: '12px', padding: '12px', background: '#f8d7da', borderRadius: '4px', color: '#721c24' }}>
              <XCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
              {testNowError}
            </div>
          )}
          {testNowResult && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: testNowResult.success ? '#d4edda' : '#f8d7da',
              borderRadius: '4px',
              color: testNowResult.success ? '#155724' : '#721c24',
            }}>
              {testNowResult.success ? <CheckCircle size={16} style={{ display: 'inline', marginRight: '8px' }} /> : <XCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />}
              {testNowResult.message}
            </div>
          )}
        </div>
      </div>

      {/* V47.15.2: Employee Picker Modals */}
      <EmployeePickerModal
        isOpen={resetEmployeePickerOpen}
        onClose={() => setResetEmployeePickerOpen(false)}
        onSelect={(employeeId, employeeName) => {
          setResetEmployeeId(employeeId);
          setResetEmployeeName(employeeName);
          setResetEmployeePickerOpen(false);
        }}
        title="휴가 초기화 대상 직원 선택"
        selectedEmployeeId={resetEmployeeId || null}
      />

      <EmployeePickerModal
        isOpen={recomputeEmployeePickerOpen}
        onClose={() => setRecomputeEmployeePickerOpen(false)}
        onSelect={(employeeId, employeeName) => {
          setRecomputeEmployeeId(employeeId);
          setRecomputeEmployeeName(employeeName);
          setRecomputeEmployeePickerOpen(false);
        }}
        title="휴가 재계산 대상 직원 선택"
        selectedEmployeeId={recomputeEmployeeId || null}
      />
    </div>
  );
}
